// 웨이터 호출 번역 및 상수
import { Droplets, FileText, Snowflake, Utensils, UtensilsCrossed } from 'lucide-react';
import type { Language, Locale } from '@/config/locales';
import type { RequestItem, WaiterRequestTranslations } from './types';

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const waiterRequestTranslations:
  & Record<Language, WaiterRequestTranslations>
  & Record<Exclude<Locale, Language>, WaiterRequestTranslations> = {
  ko: {
    title: '웨이터 호출',
    subtitle: '필요한 항목을 선택해주세요',
    messagePlaceholder: '추가 요청사항을 입력해주세요...',
    items: {
      plates: '접시',
      water: '물',
      ice: '얼음',
      napkins: '냅킨',
      utensils: '포크/나이프',
      tissues: '티슈',
    },
    wifiSection: 'WiFi 정보',
    wifiName: 'WiFi 이름',
    wifiPassword: '비밀번호',
    copyPassword: '비밀번호 복사',
    copied: '복사됨!',
    connectWifi: 'WiFi 바로 등록',
    sendRequest: '요청 보내기',
    cancel: '취소',
    requestSent: '요청이 전송되었습니다!',
    selectAtLeastOne: '최소 1개 항목을 선택해주세요',
    tableNumber: '테이블 번호',
    tableNumberPlaceholder: '테이블 번호를 입력하세요',
    tableNumberRequired: '테이블 번호를 입력해주세요',
    sending: '전송 중...',
    duplicateCall: '이미 대기 중인 호출이 있습니다',
    error: '오류가 발생했습니다',
    wifiCopied: 'WiFi 정보가 복사되었습니다!',
  },
  es: {
    title: 'Llamar mesero',
    subtitle: 'Seleccione los artículos necesarios',
    messagePlaceholder: 'Escriba solicitudes adicionales...',
    items: {
      plates: 'Platos',
      water: 'Agua',
      ice: 'Hielo',
      napkins: 'Servilletas',
      utensils: 'Tenedor/Cuchillo',
      tissues: 'Pañuelos',
    },
    wifiSection: 'Información WiFi',
    wifiName: 'Nombre WiFi',
    wifiPassword: 'Contraseña',
    copyPassword: 'Copiar contraseña',
    copied: '¡Copiado!',
    connectWifi: 'Conectar WiFi',
    sendRequest: 'Enviar solicitud',
    cancel: 'Cancelar',
    requestSent: '¡Solicitud enviada!',
    selectAtLeastOne: 'Seleccione al menos 1 artículo',
    tableNumber: 'Número de mesa',
    tableNumberPlaceholder: 'Ingrese el número de mesa',
    tableNumberRequired: 'Por favor ingrese el número de mesa',
    sending: 'Enviando...',
    duplicateCall: 'Ya hay una llamada pendiente',
    error: 'Ocurrió un error',
    wifiCopied: '¡Información WiFi copiada!',
  },
  en: {
    title: 'Call Waiter',
    subtitle: 'Select needed items',
    messagePlaceholder: 'Write additional requests...',
    items: {
      plates: 'Plates',
      water: 'Water',
      ice: 'Ice',
      napkins: 'Napkins',
      utensils: 'Fork/Knife',
      tissues: 'Tissues',
    },
    wifiSection: 'WiFi Information',
    wifiName: 'WiFi Name',
    wifiPassword: 'Password',
    copyPassword: 'Copy password',
    copied: 'Copied!',
    connectWifi: 'Connect WiFi',
    sendRequest: 'Send request',
    cancel: 'Cancel',
    requestSent: 'Request sent!',
    selectAtLeastOne: 'Please select at least 1 item',
    tableNumber: 'Table Number',
    tableNumberPlaceholder: 'Enter table number',
    tableNumberRequired: 'Please enter table number',
    sending: 'Sending...',
    duplicateCall: 'There is already a pending call',
    error: 'An error occurred',
    wifiCopied: 'WiFi info copied!',
  },
  pt: {
    title: 'Chamar garçom',
    subtitle: 'Selecione os itens necessários',
    messagePlaceholder: 'Escreva solicitações adicionais...',
    items: {
      plates: 'Pratos',
      water: 'Água',
      ice: 'Gelo',
      napkins: 'Guardanapos',
      utensils: 'Garfo/Faca',
      tissues: 'Lenços',
    },
    wifiSection: 'Informações do WiFi',
    wifiName: 'Nome do WiFi',
    wifiPassword: 'Senha',
    copyPassword: 'Copiar senha',
    copied: 'Copiado!',
    connectWifi: 'Conectar ao WiFi',
    sendRequest: 'Enviar solicitação',
    cancel: 'Cancelar',
    requestSent: 'Solicitação enviada!',
    selectAtLeastOne: 'Selecione pelo menos 1 item',
    tableNumber: 'Número da mesa',
    tableNumberPlaceholder: 'Digite o número da mesa',
    tableNumberRequired: 'Por favor, digite o número da mesa',
    sending: 'Enviando...',
    duplicateCall: 'Já existe uma chamada pendente',
    error: 'Ocorreu um erro',
    wifiCopied: 'Informações do WiFi copiadas!',
  },
  fr: {
    title: 'Appeler le serveur',
    subtitle: 'Sélectionnez les articles nécessaires',
    messagePlaceholder: 'Écrivez vos demandes supplémentaires...',
    items: {
      plates: 'Assiettes',
      water: 'Eau',
      ice: 'Glaçons',
      napkins: 'Serviettes',
      utensils: 'Fourchette/Couteau',
      tissues: 'Mouchoirs',
    },
    wifiSection: 'Informations WiFi',
    wifiName: 'Nom du WiFi',
    wifiPassword: 'Mot de passe',
    copyPassword: 'Copier le mot de passe',
    copied: 'Copié !',
    connectWifi: 'Se connecter au WiFi',
    sendRequest: 'Envoyer la demande',
    cancel: 'Annuler',
    requestSent: 'Demande envoyée !',
    selectAtLeastOne: 'Veuillez sélectionner au moins 1 article',
    tableNumber: 'Numéro de table',
    tableNumberPlaceholder: 'Saisissez le numéro de table',
    tableNumberRequired: 'Veuillez saisir le numéro de table',
    sending: 'Envoi en cours...',
    duplicateCall: 'Un appel est déjà en attente',
    error: 'Une erreur est survenue',
    wifiCopied: 'Informations WiFi copiées !',
  },
  zh: {
    title: '呼叫服务员',
    subtitle: '请选择所需项目',
    messagePlaceholder: '请输入其他要求...',
    items: {
      plates: '盘子',
      water: '水',
      ice: '冰块',
      napkins: '餐巾纸',
      utensils: '叉子/刀',
      tissues: '纸巾',
    },
    wifiSection: 'WiFi 信息',
    wifiName: 'WiFi 名称',
    wifiPassword: '密码',
    copyPassword: '复制密码',
    copied: '已复制！',
    connectWifi: '连接 WiFi',
    sendRequest: '发送请求',
    cancel: '取消',
    requestSent: '请求已发送！',
    selectAtLeastOne: '请至少选择 1 项',
    tableNumber: '桌号',
    tableNumberPlaceholder: '请输入桌号',
    tableNumberRequired: '请填写桌号',
    sending: '发送中...',
    duplicateCall: '已有等待中的呼叫',
    error: '发生错误',
    wifiCopied: 'WiFi 信息已复制！',
  },
  ja: {
    title: 'ウェイターを呼ぶ',
    subtitle: '必要な項目を選択してください',
    messagePlaceholder: '追加のご要望を入力してください...',
    items: {
      plates: '皿',
      water: '水',
      ice: '氷',
      napkins: 'ナプキン',
      utensils: 'フォーク/ナイフ',
      tissues: 'ティッシュ',
    },
    wifiSection: 'WiFi情報',
    wifiName: 'WiFi名',
    wifiPassword: 'パスワード',
    copyPassword: 'パスワードをコピー',
    copied: 'コピーしました！',
    connectWifi: 'WiFiに接続',
    sendRequest: 'リクエストを送信',
    cancel: 'キャンセル',
    requestSent: 'リクエストを送信しました！',
    selectAtLeastOne: '少なくとも1つの項目を選択してください',
    tableNumber: 'テーブル番号',
    tableNumberPlaceholder: 'テーブル番号を入力してください',
    tableNumberRequired: 'テーブル番号を入力してください',
    sending: '送信中...',
    duplicateCall: 'すでに待機中の呼び出しがあります',
    error: 'エラーが発生しました',
    wifiCopied: 'WiFi情報をコピーしました！',
  },
  hi: {
    title: 'वेटर बुलाएं',
    subtitle: 'आवश्यक वस्तुएं चुनें',
    messagePlaceholder: 'अतिरिक्त अनुरोध लिखें...',
    items: {
      plates: 'प्लेटें',
      water: 'पानी',
      ice: 'बर्फ',
      napkins: 'नैपकिन',
      utensils: 'कांटा/चाकू',
      tissues: 'टिशू',
    },
    wifiSection: 'WiFi जानकारी',
    wifiName: 'WiFi नाम',
    wifiPassword: 'पासवर्ड',
    copyPassword: 'पासवर्ड कॉपी करें',
    copied: 'कॉपी हो गया!',
    connectWifi: 'WiFi से कनेक्ट करें',
    sendRequest: 'अनुरोध भेजें',
    cancel: 'रद्द करें',
    requestSent: 'अनुरोध भेजा गया!',
    selectAtLeastOne: 'कृपया कम से कम 1 वस्तु चुनें',
    tableNumber: 'टेबल नंबर',
    tableNumberPlaceholder: 'टेबल नंबर दर्ज करें',
    tableNumberRequired: 'कृपया टेबल नंबर दर्ज करें',
    sending: 'भेजा जा रहा है...',
    duplicateCall: 'पहले से एक कॉल लंबित है',
    error: 'एक त्रुटि हुई',
    wifiCopied: 'WiFi जानकारी कॉपी हो गई!',
  },
  de: {
    title: 'Kellner rufen',
    subtitle: 'Wählen Sie die benötigten Artikel',
    messagePlaceholder: 'Schreiben Sie zusätzliche Wünsche...',
    items: {
      plates: 'Teller',
      water: 'Wasser',
      ice: 'Eis',
      napkins: 'Servietten',
      utensils: 'Gabel/Messer',
      tissues: 'Taschentücher',
    },
    wifiSection: 'WiFi-Informationen',
    wifiName: 'WiFi-Name',
    wifiPassword: 'Passwort',
    copyPassword: 'Passwort kopieren',
    copied: 'Kopiert!',
    connectWifi: 'Mit WiFi verbinden',
    sendRequest: 'Anfrage senden',
    cancel: 'Abbrechen',
    requestSent: 'Anfrage gesendet!',
    selectAtLeastOne: 'Bitte wählen Sie mindestens 1 Artikel',
    tableNumber: 'Tischnummer',
    tableNumberPlaceholder: 'Tischnummer eingeben',
    tableNumberRequired: 'Bitte geben Sie die Tischnummer ein',
    sending: 'Wird gesendet...',
    duplicateCall: 'Es gibt bereits einen ausstehenden Ruf',
    error: 'Ein Fehler ist aufgetreten',
    wifiCopied: 'WiFi-Informationen kopiert!',
  },
  it: {
    title: 'Chiama il cameriere',
    subtitle: 'Seleziona gli articoli necessari',
    messagePlaceholder: 'Scrivi richieste aggiuntive...',
    items: {
      plates: 'Piatti',
      water: 'Acqua',
      ice: 'Ghiaccio',
      napkins: 'Tovaglioli',
      utensils: 'Forchetta/Coltello',
      tissues: 'Fazzoletti',
    },
    wifiSection: 'Informazioni WiFi',
    wifiName: 'Nome WiFi',
    wifiPassword: 'Password',
    copyPassword: 'Copia password',
    copied: 'Copiato!',
    connectWifi: 'Connetti al WiFi',
    sendRequest: 'Invia richiesta',
    cancel: 'Annulla',
    requestSent: 'Richiesta inviata!',
    selectAtLeastOne: 'Seleziona almeno 1 articolo',
    tableNumber: 'Numero del tavolo',
    tableNumberPlaceholder: 'Inserisci il numero del tavolo',
    tableNumberRequired: 'Inserisci il numero del tavolo',
    sending: 'Invio in corso...',
    duplicateCall: 'C’è già una chiamata in attesa',
    error: 'Si è verificato un errore',
    wifiCopied: 'Informazioni WiFi copiate!',
  },
  ru: {
    title: 'Позвать официанта',
    subtitle: 'Выберите необходимые предметы',
    messagePlaceholder: 'Напишите дополнительные пожелания...',
    items: {
      plates: 'Тарелки',
      water: 'Вода',
      ice: 'Лёд',
      napkins: 'Салфетки',
      utensils: 'Вилка/Нож',
      tissues: 'Бумажные платочки',
    },
    wifiSection: 'Информация о WiFi',
    wifiName: 'Имя WiFi',
    wifiPassword: 'Пароль',
    copyPassword: 'Скопировать пароль',
    copied: 'Скопировано!',
    connectWifi: 'Подключиться к WiFi',
    sendRequest: 'Отправить запрос',
    cancel: 'Отмена',
    requestSent: 'Запрос отправлен!',
    selectAtLeastOne: 'Выберите хотя бы 1 предмет',
    tableNumber: 'Номер стола',
    tableNumberPlaceholder: 'Введите номер стола',
    tableNumberRequired: 'Пожалуйста, введите номер стола',
    sending: 'Отправка...',
    duplicateCall: 'Уже есть ожидающий вызов',
    error: 'Произошла ошибка',
    wifiCopied: 'Информация о WiFi скопирована!',
  },
  ar: {
    title: 'استدعاء النادل',
    subtitle: 'اختر العناصر المطلوبة',
    messagePlaceholder: 'اكتب طلبات إضافية...',
    items: {
      plates: 'أطباق',
      water: 'ماء',
      ice: 'ثلج',
      napkins: 'مناديل المائدة',
      utensils: 'شوكة/سكين',
      tissues: 'مناديل ورقية',
    },
    wifiSection: 'معلومات WiFi',
    wifiName: 'اسم WiFi',
    wifiPassword: 'كلمة المرور',
    copyPassword: 'نسخ كلمة المرور',
    copied: 'تم النسخ!',
    connectWifi: 'الاتصال بشبكة WiFi',
    sendRequest: 'إرسال الطلب',
    cancel: 'إلغاء',
    requestSent: 'تم إرسال الطلب!',
    selectAtLeastOne: 'يرجى اختيار عنصر واحد على الأقل',
    tableNumber: 'رقم الطاولة',
    tableNumberPlaceholder: 'أدخل رقم الطاولة',
    tableNumberRequired: 'يرجى إدخال رقم الطاولة',
    sending: 'جارٍ الإرسال...',
    duplicateCall: 'يوجد بالفعل نداء قيد الانتظار',
    error: 'حدث خطأ',
    wifiCopied: 'تم نسخ معلومات WiFi!',
  },
  vi: {
    title: 'Gọi phục vụ',
    subtitle: 'Vui lòng chọn các mục cần thiết',
    messagePlaceholder: 'Nhập yêu cầu bổ sung...',
    items: {
      plates: 'Đĩa',
      water: 'Nước',
      ice: 'Đá',
      napkins: 'Khăn ăn',
      utensils: 'Nĩa/Dao',
      tissues: 'Khăn giấy',
    },
    wifiSection: 'Thông tin WiFi',
    wifiName: 'Tên WiFi',
    wifiPassword: 'Mật khẩu',
    copyPassword: 'Sao chép mật khẩu',
    copied: 'Đã sao chép!',
    connectWifi: 'Kết nối WiFi',
    sendRequest: 'Gửi yêu cầu',
    cancel: 'Hủy',
    requestSent: 'Đã gửi yêu cầu!',
    selectAtLeastOne: 'Vui lòng chọn ít nhất 1 mục',
    tableNumber: 'Số bàn',
    tableNumberPlaceholder: 'Nhập số bàn',
    tableNumberRequired: 'Vui lòng nhập số bàn',
    sending: 'Đang gửi...',
    duplicateCall: 'Đã có một cuộc gọi đang chờ',
    error: 'Đã xảy ra lỗi',
    wifiCopied: 'Đã sao chép thông tin WiFi!',
  },
  th: {
    title: 'เรียกพนักงาน',
    subtitle: 'กรุณาเลือกรายการที่ต้องการ',
    messagePlaceholder: 'พิมพ์คำขอเพิ่มเติม...',
    items: {
      plates: 'จาน',
      water: 'น้ำ',
      ice: 'น้ำแข็ง',
      napkins: 'ผ้าเช็ดปาก',
      utensils: 'ส้อม/มีด',
      tissues: 'กระดาษทิชชู',
    },
    wifiSection: 'ข้อมูล WiFi',
    wifiName: 'ชื่อ WiFi',
    wifiPassword: 'รหัสผ่าน',
    copyPassword: 'คัดลอกรหัสผ่าน',
    copied: 'คัดลอกแล้ว!',
    connectWifi: 'เชื่อมต่อ WiFi',
    sendRequest: 'ส่งคำขอ',
    cancel: 'ยกเลิก',
    requestSent: 'ส่งคำขอแล้ว!',
    selectAtLeastOne: 'กรุณาเลือกอย่างน้อย 1 รายการ',
    tableNumber: 'หมายเลขโต๊ะ',
    tableNumberPlaceholder: 'กรอกหมายเลขโต๊ะ',
    tableNumberRequired: 'กรุณากรอกหมายเลขโต๊ะ',
    sending: 'กำลังส่ง...',
    duplicateCall: 'มีการเรียกที่รออยู่แล้ว',
    error: 'เกิดข้อผิดพลาด',
    wifiCopied: 'คัดลอกข้อมูล WiFi แล้ว!',
  },
  nl: {
    title: 'Ober roepen',
    subtitle: 'Selecteer de benodigde items',
    messagePlaceholder: 'Schrijf aanvullende verzoeken...',
    items: {
      plates: 'Borden',
      water: 'Water',
      ice: 'IJs',
      napkins: 'Servetten',
      utensils: 'Vork/Mes',
      tissues: 'Tissues',
    },
    wifiSection: 'WiFi-informatie',
    wifiName: 'WiFi-naam',
    wifiPassword: 'Wachtwoord',
    copyPassword: 'Wachtwoord kopiëren',
    copied: 'Gekopieerd!',
    connectWifi: 'Verbinden met WiFi',
    sendRequest: 'Verzoek verzenden',
    cancel: 'Annuleren',
    requestSent: 'Verzoek verzonden!',
    selectAtLeastOne: 'Selecteer minstens 1 item',
    tableNumber: 'Tafelnummer',
    tableNumberPlaceholder: 'Voer het tafelnummer in',
    tableNumberRequired: 'Voer alstublieft het tafelnummer in',
    sending: 'Verzenden...',
    duplicateCall: 'Er is al een oproep in behandeling',
    error: 'Er is een fout opgetreden',
    wifiCopied: 'WiFi-informatie gekopieerd!',
  },
};

// 요청 가능한 아이템 목록
export const requestItems: RequestItem[] = [
  { id: 'plates', icon: <UtensilsCrossed className="h-5 w-5" />, labelKey: 'plates' },
  { id: 'water', icon: <Droplets className="h-5 w-5" />, labelKey: 'water' },
  { id: 'ice', icon: <Snowflake className="h-5 w-5" />, labelKey: 'ice' },
  { id: 'napkins', icon: <FileText className="h-5 w-5" />, labelKey: 'napkins' },
  { id: 'utensils', icon: <Utensils className="h-5 w-5" />, labelKey: 'utensils' },
  { id: 'tissues', icon: <FileText className="h-5 w-5" />, labelKey: 'tissues' },
];
